import CustomButton from '@/components/atoms/CustomButton';
import LogoSpinner from '@/components/atoms/LogoSpinner';
import useToast from '@/hooks/useToast';
import { CloseIcon } from '@chakra-ui/icons';
import { Box, HStack, IconButton, Input, Text, VStack } from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import VideoPlayer from '@/modules/content/common/VideoPlayer';
import FieldError from '@/modules/content/common/FieldError';
import { TVideoSource } from '@/modules/content/creation/model/types';
import { useYouTubeDurationValidation } from '@/modules/content/creation/model/useYouTubeDurationValidation';
import { compressVideo, createFFmpeg } from '@/modules/content/creation/model/ffmpegVideoCompression';

const MAX_VIDEO_SIZE_MB = 500;
export const MAX_VIDEO_DURATION_SECONDS = 90;
const ACCEPTED_VIDEO = 'video/mp4,video/quicktime,video/webm';

type Props = {
  source: TVideoSource;
  onSourceChange: (source: TVideoSource) => void;
  file: File | null;
  onFileChange: (file: File | null) => void;
  youtubeUrl: string;
  onYoutubeUrlChange: (url: string) => void;
  onProcessingErrorChange?: (message: string | null) => void;
  onCompressingChange?: (compressing: boolean) => void;
  onYoutubeDurationCheckingChange?: (checking: boolean) => void;
  hasExistingVideo?: boolean;
  muxStatus?: string | null;
  muxPlaybackId?: string | null;
  error?: string;
  isCreateReel?: boolean;
};

const ToggleButton = ({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) => (
  <Box
    as="button"
    type="button"
    onClick={onClick}
    flex={1}
    paddingY="8px"
    fontSize="13px"
    fontWeight={700}
    textTransform="uppercase"
    letterSpacing="0.3px"
    borderRadius="8px"
    color={active ? 'extra.white' : 'text.mediumBlue'}
    backgroundColor={active ? 'primary.500' : 'transparent'}
    transition="all 0.15s ease"
  >
    {label}
  </Box>
);

const VideoSourceField = ({
  source,
  onSourceChange,
  file,
  onFileChange,
  youtubeUrl,
  onYoutubeUrlChange,
  onProcessingErrorChange,
  onCompressingChange,
  onYoutubeDurationCheckingChange,
  error,
  hasExistingVideo = false,
  muxStatus,
  muxPlaybackId,
  isCreateReel = false,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const [errorText, setErrorText] = useState('');

  const clearInputValue = () => {
    if (inputRef.current) inputRef.current.value = '';
  };

  const setValidationError = ({ message, title }: { message: string; title: string }) => {
    setErrorText(message);
    clearInputValue();

    toast({
      status: 'warning',
      title,
      description: message,
      position: 'bottom-right',
    });
  };

  const [loaded, setLoaded] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [progress, setProgress] = useState(0);

  const ffmpegRef = useRef(createFFmpeg());
  const compressionIdRef = useRef(0);
  const compressionCanceledRef = useRef(false);

  useEffect(() => {
    onCompressingChange?.(compressing);

    return () => onCompressingChange?.(false);
  }, [compressing, onCompressingChange]);

  useYouTubeDurationValidation({
    source,
    youtubeUrl,
    onErrorChange: onProcessingErrorChange,
    onCheckingChange: onYoutubeDurationCheckingChange,
  });

  const resetFFmpeg = () => {
    ffmpegRef.current.terminate();
    ffmpegRef.current = createFFmpeg();
    setLoaded(false);
    setProgress(0);
  };

  const handleCancelCompression = () => {
    compressionCanceledRef.current = true;
    compressionIdRef.current += 1;
    resetFFmpeg();
    setCompressing(false);
    clearFile();
  };

  const handlePick = (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files?.[0];
    if (!picked) return;
    if (picked.size > MAX_VIDEO_SIZE_MB * 1024 * 1024) {
      setValidationError({
        title: 'File too large.',
        message: `Video must be under ${MAX_VIDEO_SIZE_MB}MB`,
      });
      return;
    }

    clearFile();

    const videoUrl = URL.createObjectURL(picked);
    const videoElement = document.createElement('video');
    videoElement.preload = 'metadata';
    videoElement.src = videoUrl;

    videoElement.onloadedmetadata = async () => {
      URL.revokeObjectURL(videoUrl);

      if (videoElement.duration > MAX_VIDEO_DURATION_SECONDS) {
        setValidationError({
          title: 'Video too long.',
          message: `Video must be ${MAX_VIDEO_DURATION_SECONDS} seconds or less.`,
        });
        return;
      }

      setErrorText('');
      onProcessingErrorChange?.(null);
      compressionCanceledRef.current = false;
      const compressionId = ++compressionIdRef.current;
      try {
        const compressedFile = await compressVideo(ffmpegRef.current, picked, {
          loaded,
          onLoaded: () => {
            if (compressionId === compressionIdRef.current) setLoaded(true);
          },
          onProgress: (nextProgress) => {
            if (compressionId === compressionIdRef.current) setProgress(nextProgress);
          },
          onCompressingChange: (nextCompressing) => {
            if (compressionId === compressionIdRef.current) setCompressing(nextCompressing);
          },
        });

        if (compressionId !== compressionIdRef.current) return;

        onProcessingErrorChange?.(null);
        onFileChange(compressedFile);
      } catch {
        if (compressionCanceledRef.current || compressionId !== compressionIdRef.current) {
          compressionCanceledRef.current = false;
          return;
        }

        const message = 'We could not compress this video file. Please try another file.';

        onFileChange(null);
        onProcessingErrorChange?.(message);
        setValidationError({
          title: 'Video compression failed.',
          message,
        });
      }
    };

    videoElement.onerror = () => {
      URL.revokeObjectURL(videoUrl);
      setValidationError({
        message: 'We could not read this video file. Please try another file.',
        title: 'Invalid video file.',
      });
    };
  };

  const clearFile = () => {
    setErrorText('');
    onProcessingErrorChange?.(null);
    onFileChange(null);
    clearInputValue();
  };

  const handleSourceChange = (nextSource: TVideoSource) => {
    setErrorText('');
    onProcessingErrorChange?.(null);
    onSourceChange(nextSource);
  };

  return (
    <VStack align="stretch" gap="8px" width="100%">
      {/* Segmented toggle */}
      <HStack
        padding="3px"
        borderRadius="10px"
        border="1px solid"
        borderColor="background.separatorBlue"
        backgroundColor="white"
        gap="3px"
      >
        <ToggleButton label="Upload File" active={source === 'mux'} onClick={() => handleSourceChange('mux')} />
        <ToggleButton
          label="YouTube Link"
          active={source === 'youtube'}
          onClick={() => handleSourceChange('youtube')}
        />
      </HStack>

      {source === 'mux' ? (
        <VStack align="stretch" gap={compressing ? 0 : '6px'}>
          {compressing ? (
            <VStack gap={0}>
              <HStack justifyContent={'center'}>
                <Text fontSize="12px" color="text.mediumBlue" className="my-2" textAlign={'center'}>
                  Compressing {progress}%... This will take a minute.
                </Text>
                <CustomButton
                  isTransparent
                  label="CANCEL"
                  height={'20px'}
                  labelColor="extra.red"
                  labelStyle={{ fontWeight: 900 }}
                  hoverOpacityDrop
                  width="12%"
                  onClick={handleCancelCompression}
                />
              </HStack>
            </VStack>
          ) : (
            <CustomButton
              label={
                isCreateReel && !file && !hasExistingVideo
                  ? 'Choose Reel File'
                  : `Upload ${file || hasExistingVideo ? 'Different' : ''} Reel File`
              }
              iconBeforeLabelJSX={compressing ? <LogoSpinner size="18px" variant="light" /> : undefined}
              backgroundColor="primary.500"
              labelColor="extra.white"
              width="100%"
              onClick={() => inputRef.current?.click()}
              disabled={compressing}
            />
          )}
          <Text fontSize="12px" color="text.mediumGray" textAlign={'center'}>
            {compressing
              ? 'Keep filling out the form while we get the video ready.'
              : `MP4, MOV, WebM up to ${MAX_VIDEO_SIZE_MB} MB and up to ${MAX_VIDEO_DURATION_SECONDS} seconds`}
          </Text>
          {errorText.length > 0 && <Text variant={'error'}>{errorText}</Text>}
          {hasExistingVideo && !file && !compressing && (
            <VStack align="stretch" gap="6px">
              <Text fontSize="13px" color="text.darkBlue">
                Existing video
              </Text>
              <VideoPlayer source="mux" muxStatus={muxStatus} muxPlaybackId={muxPlaybackId} />
            </VStack>
          )}
          {file && (
            <HStack
              justify="space-between"
              border="1px solid"
              borderColor="background.separatorBlue"
              borderRadius="8px"
              paddingX="12px"
              paddingY="8px"
              backgroundColor="white"
            >
              <Text fontSize="13px" color="text.darkBlue" noOfLines={1}>
                {file.name}
              </Text>
              <IconButton
                aria-label="Remove file"
                icon={<CloseIcon boxSize={2.5} />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                onClick={clearFile}
              />
            </HStack>
          )}
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_VIDEO}
            hidden
            onChange={handlePick}
            disabled={compressing}
          />
        </VStack>
      ) : (
        <Input
          backgroundColor="white"
          height={isCreateReel ? '54px' : undefined}
          minHeight={isCreateReel ? '54px' : undefined}
          className="custom-input"
          placeholder="YouTube link e.g. www.youtube.com/watch?v=..."
          value={youtubeUrl}
          onChange={(e) => onYoutubeUrlChange(e.target.value)}
        />
      )}

      <FieldError error={error} />
    </VStack>
  );
};

export default VideoSourceField;
